import type {RevaLifeCycleState} from "@reva/shared/lifecycle-state";
import type {RevaValuationPurpose} from "@reva/shared/valuation-purpose";
import type {ValueOrderType} from "@reva/shared/value-order";

/** Sort direction for a search query. `null` means unsorted (normalized to "DESC" before sending). */
export type RevaSortOrder = "ASC" | "DESC" | null;

/**
 * Auf welchen der Startseiten-Tabs (#19113) eine Suchanfrage eingeschränkt ist: "WIP" = die eigenen,
 * noch bearbeitbaren Bewertungen des Users; "POOL" = nicht zugewiesene Bewertungen, die jeder mit den
 * passenden Rechten übernehmen könnte; "CORRECTION" = alle finalisierten Bewertungen, auf die der User
 * Zugriff hat, für Inhaber von reva-lifecycle-correction ("Korrekturen"); "TEAMADMIN" = alle Bewertungen aller
 * User (der "Alle/Team"-Tab, nur mit reva-ownership-admin); "REVISION" = alle Bewertungen aller User mit
 * mindestens einem Snapshot (PreFinal/Final/Correction), read-only, für Inhaber von
 * reva-lifecycle-revision-readonly (der exklusive "Alle Bewertungen"-Tab, #19125); "RESEARCH" = die eigenen
 * Research-Abfragen ("Research-Abfragen", nur mit reva-purpose-research, #19481) — Research-Abfragen
 * erscheinen ausschließlich dort, alle anderen Tabs zeigen nur Bewertungen. Das Backend wendet die
 * komplette Owner-/Lifecycle-/Permission-Logik für den jeweiligen Tab an; das Frontend markiert die Anfrage
 * nur damit, berechnet die Filterung nie selbst.
 */
export type RevaFilterTab = "WIP" | "POOL" | "CORRECTION" | "TEAMADMIN" | "REVISION" | "RESEARCH";

/** All start-page tabs (record keys of the per-tab search state). */
export const REVA_FILTER_TABS: readonly RevaFilterTab[] = [
    "WIP",
    "POOL",
    "CORRECTION",
    "TEAMADMIN",
    "REVISION",
    "RESEARCH",
];

/**
 * Grenzen des Live-Freitextfilters (#19113/#19380) — Spiegel der Java-Konstanten in
 * `SearchValuationsRequestApiModel` (FREE_TEXT_MIN_LENGTH/-MAX_LENGTH/-MAX_TOKENS); bei einer Änderung dort
 * hier mitziehen. Unterhalb der Mindestlänge schränkt der Filter nie ein, oberhalb der Obergrenzen lehnt die
 * API die Anfrage ab (HTTP 422): die Suchkosten wachsen linear mit der Zahl der Suchwörter, ein in die
 * Suchzeile eingefügter Textblock erzeugte eine Query, die Elasticsearch abbrach. Der Client filtert deshalb
 * gar nicht, statt eine Anfrage zu schicken, die nur scheitern kann.
 */
export const FREE_TEXT_LIMITS = {minLength: 3, maxLength: 200, maxTokens: 12} as const;

/**
 * Eingabeparameter einer Bewertungs-Suchanfrage (POST {@link RevaApi.PATH_VALUATIONS_SEARCH}):
 * Paging, Sortierung, der Statusfilter und der globale Live-Freitextfilter. Leere Filter werden aus
 * dem Request-Body weggelassen.
 */
export interface SearchValuationsRequest {
    pageIndex: number;
    pageSize: number;
    sortField: string;
    sortOrder: RevaSortOrder;
    /** Lifecycle states to include (OR-combined); empty/omitted = no lifecycle filter. */
    filterLifeCycleState?: RevaLifeCycleState[] | null;
    /** Fixed per-tab filter (#19113), not user-changeable; see {@link RevaFilterTab}. */
    filterTab?: RevaFilterTab | null;
    /**
     * Globaler Live-Freitextfilter aus der Startseiten-Suchzeile (matcht über viele Spalten).
     * null/leer/kürzer als 3 Zeichen nach Trim = kein Filter (Länge wird serverseitig nochmals geprüft).
     */
    filterFreeText?: string | null;
}

/** A value order attached to a search-result valuation. */
export interface SearchValuationItemOrder {
    orderType: ValueOrderType;
    valueOrderGuid: string;
    valueExternalOrderNumber: string;
}

/** A single valuation row in the search result. */
export interface SearchValuationItem {
    revaId: string;
    createdDate: string;
    createdUser: string;
    createdUserName?: string | null;
    lastChangedDate: string;
    lastChangedUser: string;
    lastChangedUserName?: string | null;
    owner?: string | null;
    ownerFullName?: string | null;
    license?: string | null;
    licenseDisplayName?: string | null;
    address: string;
    referenceAddress?: string | null;
    finalized: boolean;
    lifeCycleState: RevaLifeCycleState;
    orders: SearchValuationItemOrder[];
    /** Zeitpunkt der letzten (Re-)Finalisierung; nur bei bereits finalisierten Bewertungen gesetzt. */
    finalizeDate?: string | null;
    finalizeUser?: string | null;
    finalizeUserName?: string | null;
    /** Zweck (#19481); fehlt bei älterem Backend/Altdokument = Bewertung (`effectivePurpose`). */
    purpose?: RevaValuationPurpose | null;
    /** Zeitpunkt der automatischen Löschung ohne weitere Nutzung; nur bei Zwecken mit Frist (Research). */
    retentionExpiresAt?: string | null;
}

/** Paged result of a valuations search. */
export interface SearchValuationResult {
    count: number;
    pages: number;
    valuations: SearchValuationItem[];
    /**
     * The lifecycle states the searched tab can ever contain for the calling user (tab semantics +
     * permissions, in ordinal order) — drives which status-filter checkboxes are offered. Optional
     * for rollout compatibility (an older backend omits it; the frontend then offers all states).
     */
    possibleLifeCycleStates?: RevaLifeCycleState[];
}
